import type { LandingConfig } from "../components/SeoLandingPage";

/* C3 (2026-11-03): muhurat-by-type monthly pages, Nov + Dec 2026.

   DATES ARE CROSS-CHECKED, 2026-11-03, against published panchang lists:
   - Vivah: DrikPanchang + SmartPuja agree on all 11 dates (same list as the
     live /blog/shadi-muhurat-nov-dec-2026 post).
   - Griha pravesh: DrikPanchang (VedicFeed mirrors it) + PanchangBodh, with
     GaneshaSpeaks as a third check. Single-publisher dates sit in a separate
     "split" table, never in the main one.
   - Mundan + naamkaran: DrikPanchang publishes no yearly list for these, so
     the main tables are dates where GaneshaSpeaks and VedicFeed/Astrotalk (or
     PanchangBodh) agree. Weekday, tithi and nakshatra for those dates are
     taken from DrikPanchang's New Delhi month panchang, because the
     publishers' own nakshatra labels did not match it. PanchangBodh lists
     ZERO mundan dates for Nov and Dec 2026; the pages say so.
   - Kharmas: Dhanu Sankranti is Wednesday, 16 December 2026 (DrikPanchang).
   - Devuthani Ekadashi: Fri 20 Nov 2026 (Smarta), Vaishnava 21 Nov, per
     DrikPanchang. These pages only say "Devuthani Ekadashi ke baad".
   NO timings anywhere: lagna windows depend on the city, every page points
   to /muhurat and /panchang for that. */

const baseChips = [
  { to: "/muhurat", label: "Muhurat checker" },
  { to: "/panchang", label: "Aaj ka panchang" },
  { to: "/free-kundli", label: "Free kundli" },
];

const pmDisclaimer =
  "Dates published panchang lists se cross-check ki gayi hain (New Delhi ke hisaab se). Lagna aur exact samay sheher ke saath badalta hai; antim faisla apne parivaar ke pandit ji aur sthaniya panchang ka hai. Muhurat parampara ki guidance hai, kisi parinaam ki guarantee nahi.";

const panditNote =
  "Yeh list shortlist hai, final faisla nahi. Muhurat ka asli hissa lagna hai, jo aapke sheher ke sunrise aur us din ke grahon par depend karta hai. Isliye chuni hui date apne parivaar ke pandit ji se, ya apne sheher ke panchang se, ek baar zaroor confirm kijiye. Humara muhurat checker city chunne par wahi ka samay dikhata hai.";

/* ------------------------------------------------------------------ VIVAH */

const vivahRules = {
  h2: "Vivah muhurat classically kaise chuna jaata hai",
  body: [
    "Vivah muhurat koi rahasya nahi, panchang ke kuch saaf filters hain. Har filter kuch din kaat deta hai, aur jo bachte hain wahi list me aate hain. Isi wajah se kisi ek date par poore sheher ki shaadiyan ek saath hoti hain.",
  ],
  bullets: [
    "Chaturmas nahi: Devshayani se Devuthani Ekadashi tak ke chaar mahine vivah ke liye varjit maane jaate hain.",
    "Kharmas nahi: jab Surya Dhanu ya Meena rashi me hota hai, us mahine bhar vivah roke jaate hain. 2026 me Dhanu Sankranti 16 December ko hai.",
    "Guru aur Shukra asta na hon: dono grah Surya ke itne paas na hon ki unki roshni doob jaaye.",
    "Shubh nakshatra: Rohini, Mrigashira, Magha, Uttara Phalguni, Hasta, Swati, Anuradha, Mula, Uttara Ashadha, Uttara Bhadrapada aur Revati vivah ke liye shubh maane jaate hain.",
    "Tithi: Amavasya aur kuch khaas tithiyan avoid ki jaati hain.",
    "Bhadra aur Rahu kaal: phere aur kanyadaan jaise mukhya karm in samayon se bachakar rakhe jaate hain.",
    "Lagna shuddhi: aakhri filter lagna hai, jo sheher ke hisaab se badalta hai.",
  ],
};

const vivahFaqCommon = [
  {
    q: "Kya bina muhurat ke shadi nahi ho sakti?",
    a: "Ho sakti hai. Court marriage aur kai parivaar bina panchang dekhe bhi shadi karte hain. Muhurat parampara me ek shubh shuruaat dene ke liye hai, rishte ki guarantee ke liye nahi. Dono kundliyon ka milan aur dono logon ki samajh zyada matter karti hai.",
  },
  {
    q: "Alag websites par alag dates kyun dikhti hain?",
    a: "Kyunki lagna window sheher ke sunrise par depend karti hai, aur kuch publishers kuch nakshatra ya tithi ko alag tarah tolte hain. Humne sirf woh dates rakhi hain jin par kam se kam do panchang sehmat hain, aur jahan nakshatra alag likha mila wahan woh bhi bataya hai.",
  },
];

export const vivahNov2026: LandingConfig = {
  url: "https://astroaura.ai/vivah-muhurat-november-2026",
  wide: true,
  title: "Vivah Muhurat November 2026: Shubh Shadi Dates | Aura AI",
  description:
    "November 2026 me vivah ke 4 shubh din: 21, 24, 25 aur 26 November. Nakshatra, tithi, din, aur Devuthani Ekadashi ke baad season kyun khulta hai, sab yahan.",
  eyebrow: "Vivah Muhurat November 2026",
  h1Pre: "Vivah Muhurat November 2026:",
  h1Accent: "21, 24, 25, 26 November",
  sub: "Devuthani Ekadashi ke baad shadi ka season khulta hai. November me sirf chaar shubh vivah din hain, nakshatra aur tithi ke saath.",
  primaryCta: "Apni date kundli se check kijiye",
  secondaryCta: "Muhurat checker kholiye",
  lede: [
    "November 2026 me vivah muhurat ke chaar din hain: Saturday 21 November, Tuesday 24 November, Wednesday 25 November aur Thursday 26 November. Yeh dates DrikPanchang aur SmartPuja dono ki lists me hain. Mahine ka pehla hissa Chaturmas me hai, isliye season Devuthani Ekadashi ke baad hi shuru hota hai, aur December ke beech Kharmas lagne se pehle sirf kuch hafte milte hain.",
  ],
  heroFacts: ["4 shubh din", "Devuthani ke baad", "Do panchang se verified"],
  sections: [
    {
      h2: "November 2026 ki shubh vivah dates",
      table: {
        head: ["Date", "Nakshatra", "Tithi"],
        rows: [
          ["21 Nov, Sat", "Revati", "Dwadashi, phir Trayodashi"],
          ["24 Nov, Tue", "Rohini (raat ka muhurat)", "Pratipada"],
          ["25 Nov, Wed", "Rohini, phir Mrigashira", "Pratipada, Dwitiya"],
          ["26 Nov, Thu", "Mrigashira", "Dwitiya, Tritiya"],
        ],
        caption:
          "Sources: DrikPanchang aur SmartPuja (dono me chaaron dates). 21 November ki tithi DrikPanchang Dwadashi aur SmartPuja Trayodashi likhta hai; us din dono tithiyan padti hain.",
      },
      body: [
        "24 November ka muhurat raat me shuru hota hai, kyunki Rohini nakshatra us raat lagta hai. 25 November sabse lamba din hai, lagbhag pura din Rohini aur Mrigashira me. Lagna ka exact samay apne sheher ke liye muhurat checker me dekhiye.",
      ],
      chips: [
        ...baseChips,
        { to: "/blog/shadi-muhurat-nov-dec-2026", label: "Shadi muhurat Nov-Dec 2026" },
      ],
    },
    {
      h2: "November ka pehla hissa khaali kyun hai",
      body: [
        "1 se lagbhag 20 November tak Chaturmas chal raha hota hai, jo Devuthani (Prabodhini) Ekadashi par khatam hota hai. Parampara me mana jaata hai ki is din Vishnu ji yog nidra se jaagte hain, aur agle din Tulsi Vivah ke saath shaadiyon ka season khulta hai. Kuchh parivaar Devuthani ke din ko hi abujh muhurat maankar shadi kar lete hain.",
        "Ekadashi ki exact tarikh par panchangon me ek din ka antar dikhta hai, isliye apne parivaar ki parampara aur sthaniya panchang se din confirm kijiye.",
      ],
    },
    vivahRules,
    {
      h2: "Pandit ji aur city panchang se confirm kijiye",
      body: [panditNote],
    },
    {
      h2: "Date pakki karne se pehle kundli me kya dekhein",
      body: [
        "Generic list sabke liye ek hai, lekin shadi do logon ki hai. Dono kundliyon ka gun milan, manglik sthiti, aur dono ki chal rahi dasha dekh lena samajhdaari hai. Agar abhi kundli nahi bani, to free kundli se shuruaat kijiye.",
      ],
      chips: [
        { to: "/kundli-matching", label: "Kundli matching" },
        { to: "/manglik-calculator", label: "Manglik calculator" },
        { to: "/vivah-muhurat-december-2026", label: "Vivah muhurat December 2026" },
      ],
    },
  ],
  faqs: [
    {
      q: "November 2026 me shadi ke muhurat kaun se hain?",
      a: "21, 24, 25 aur 26 November 2026. Nakshatra Revati, Rohini aur Mrigashira hain. Lagna ka samay sheher ke hisaab se alag hota hai, isliye final time apne panchang ya pandit ji se confirm kijiye.",
    },
    {
      q: "November ke pehle hafton me shadi kyun nahi hoti?",
      a: "Kyunki tab tak Chaturmas chal raha hota hai, jo Devuthani Ekadashi par khatam hota hai. Is dauraan vivah jaise bade sanskar parampara se roke jaate hain.",
    },
    {
      q: "November ke baad kitna samay bachta hai?",
      a: "December ke shuruaati do hafte. 16 December 2026 ko Dhanu Sankranti ke saath Kharmas shuru ho jaata hai, aur phir vivah Makar Sankranti ke baad hi khulte hain.",
    },
    ...vivahFaqCommon,
  ],
  schemaName: "Vivah Muhurat November 2026",
  schemaDescription:
    "Auspicious Hindu marriage dates in November 2026: 21, 24, 25 and 26 November, with nakshatra and tithi, cross-checked across two panchang publishers.",
  finalHeading: "Date chuniye, apne chart ke saath",
  finalText:
    "Free kundli banaiye aur AI jyotishi se poochhiye ki in dates me se aapke liye kaunsi behtar baithti hai.",
  articleSection: "Muhurat",
  disclaimer: pmDisclaimer,
  relatedLinks: [
    { to: "/vivah-muhurat-december-2026", label: "vivah muhurat December 2026" },
    { to: "/griha-pravesh-muhurat-november-2026", label: "griha pravesh muhurat November 2026" },
  ],
};

export const vivahDec2026: LandingConfig = {
  url: "https://astroaura.ai/vivah-muhurat-december-2026",
  wide: true,
  title: "Vivah Muhurat December 2026: Kharmas Se Pehle | Aura AI",
  description:
    "December 2026 me vivah ke 7 shubh din: 2, 3, 4, 5, 6, 11 aur 12 December. 16 December se Kharmas, uske baad January tak koi vivah muhurat nahi. Puri list.",
  eyebrow: "Vivah Muhurat December 2026",
  h1Pre: "Vivah Muhurat December 2026:",
  h1Accent: "2 se 12 December",
  sub: "Saat shubh din, sab mahine ke pehle do hafton me. 16 December ko Kharmas lagta hai, aur uske baad December me koi vivah muhurat nahi hai.",
  primaryCta: "Apni date kundli se check kijiye",
  secondaryCta: "Muhurat checker kholiye",
  lede: [
    "December 2026 me vivah muhurat ke saat din hain: 2, 3, 4, 5, 6, 11 aur 12 December. Yeh saaton dates DrikPanchang aur SmartPuja dono ki lists me hain. Wednesday, 16 December ko Surya Dhanu rashi me jaata hai aur Kharmas shuru hota hai, isliye 13 December se mahine ke ant tak koi vivah muhurat nahi hai. Yeh jaankari bhi utni hi kaam ki hai: agar aapki planning second half ki thi, to agla season Makar Sankranti ke baad khulega.",
  ],
  heroFacts: ["7 shubh din", "Kharmas: 16 Dec se", "Do panchang se verified"],
  sections: [
    {
      h2: "December 2026 ki shubh vivah dates",
      table: {
        head: ["Date", "Nakshatra", "Tithi"],
        rows: [
          ["2 Dec, Wed", "Uttara Phalguni", "Navami, Dashami"],
          ["3 Dec, Thu", "Uttara Phalguni, phir Hasta", "Dashami, raat me Ekadashi"],
          ["4 Dec, Fri", "Hasta, phir Chitra", "Ekadashi"],
          ["5 Dec, Sat", "Swati", "Dwadashi, Trayodashi"],
          ["6 Dec, Sun", "Swati", "Trayodashi"],
          ["11 Dec, Fri", "Uttara Ashadha (raat ka muhurat); SmartPuja: Mula", "Tritiya"],
          ["12 Dec, Sat", "Uttara Ashadha", "Tritiya, Chaturthi"],
        ],
        caption:
          "Sources: DrikPanchang aur SmartPuja (dono me saaton dates). 11 December ka nakshatra do lists me alag likha hai: DrikPanchang ka muhurat raat me Uttara Ashadha me padta hai, SmartPuja Mula likhta hai.",
      },
      body: [
        "11 December ka muhurat raat me shuru hokar 12 December ki subah tak jaata hai, isliye kai parivaar ise 11-12 December ki raat ki shadi ki tarah plan karte hain. Exact lagna apne sheher ke liye muhurat checker me dekhiye.",
      ],
      chips: [
        ...baseChips,
        { to: "/blog/shadi-muhurat-nov-dec-2026", label: "Shadi muhurat Nov-Dec 2026" },
      ],
    },
    {
      h2: "16 December ke baad: Kharmas me vivah kyun nahi",
      body: [
        "Jab Surya Guru ki rashi Dhanu me hota hai, us mahine ko Kharmas (Malmas bhi kehte hain kuchh kshetron me) kaha jaata hai. Parampara me is dauraan vivah, griha pravesh aur mundan jaise shubh sanskar roke jaate hain. 2026 me Dhanu Sankranti Wednesday, 16 December ko hai, aur Kharmas Makar Sankranti par khatam hota hai.",
        "Iska seedha matlab: December 2026 ke aakhri do hafton me koi panchang vivah muhurat nahi deta. Agar venue ya chhuttiyon ki wajah se aap late December soch rahe the, to ya to 12 December tak ki date dekhiye, ya January 2027 me Makar Sankranti ke baad ki.",
      ],
      chips: [{ to: "/makar-sankranti-2027", label: "Makar Sankranti 2027" }],
    },
    vivahRules,
    {
      h2: "Pandit ji aur city panchang se confirm kijiye",
      body: [panditNote],
    },
    {
      h2: "Date ke saath kundli bhi",
      body: [
        "Date sabke liye ek hai, kundli nahi. Shadi pakki karne se pehle gun milan, manglik sthiti aur dono ki dasha dekh lijiye. Kisi khaas uljhan ke liye AI jyotishi se seedha poochh sakte hain.",
      ],
      chips: [
        { to: "/kundli-matching", label: "Kundli matching" },
        { to: "/manglik-calculator", label: "Manglik calculator" },
        { to: "/vivah-muhurat-november-2026", label: "Vivah muhurat November 2026" },
      ],
    },
  ],
  faqs: [
    {
      q: "December 2026 me shadi ki shubh dates kaun si hain?",
      a: "2, 3, 4, 5, 6, 11 aur 12 December 2026. Nakshatra Uttara Phalguni, Hasta, Chitra, Swati aur Uttara Ashadha hain.",
    },
    {
      q: "Kya 25 December ya New Year eve par shadi ka muhurat hai?",
      a: "Nahi. 16 December 2026 se Kharmas shuru ho jaata hai, aur panchang lists me December ke second half me koi vivah muhurat nahi hai. Agla season Makar Sankranti ke baad January 2027 me khulta hai.",
    },
    {
      q: "Kharmas kab tak rahega?",
      a: "Dhanu Sankranti (16 December 2026) se Makar Sankranti tak, yaani lagbhag ek mahina. Makar Sankranti ki exact date apne panchang se dekhiye.",
    },
    ...vivahFaqCommon,
  ],
  schemaName: "Vivah Muhurat December 2026",
  schemaDescription:
    "Auspicious Hindu marriage dates in December 2026: 2, 3, 4, 5, 6, 11 and 12 December. Kharmas begins 16 December, so there are no vivah muhurats after that.",
  finalHeading: "Kharmas se pehle, sahi date",
  finalText:
    "Free kundli banaiye aur AI jyotishi se poochhiye ki in saat dinon me se aapke liye kaunsa behtar hai.",
  articleSection: "Muhurat",
  disclaimer: pmDisclaimer,
  relatedLinks: [
    { to: "/vivah-muhurat-november-2026", label: "vivah muhurat November 2026" },
    { to: "/griha-pravesh-muhurat-december-2026", label: "griha pravesh muhurat December 2026" },
  ],
};

/* ---------------------------------------------------------- GRIHA PRAVESH */

const grihaRules = {
  h2: "Griha pravesh muhurat classically kaise chuna jaata hai",
  body: [
    "Shastron me griha pravesh teen tarah ka bataya gaya hai: apurva (naye ghar me pehli baar), sapurva (lambi yatra ke baad apne ghar lautna) aur dvandva (renovation ya kisi vipda ke baad dobara pravesh). Sabse sakht niyam apurva pravesh par lagte hain.",
  ],
  bullets: [
    "Sthir aur mridu nakshatra: Rohini, Mrigashira, Uttara Phalguni, Chitra, Anuradha, Uttara Ashadha, Uttara Bhadrapada aur Revati aksar chune jaate hain. Upar ki zyaadatar dates inhi par hain.",
    "Chaturmas aur Kharmas: kai parampara in dono me naye ghar ka pravesh taalti hain. Kuchh panchang lists phir bhi dates dikhati hain; neeche table me aise din alag mark kiye gaye hain.",
    "Guru aur Shukra asta na hon.",
    "Rikta tithiyan (Chaturthi, Navami, Chaturdashi) aur Amavasya aam taur par avoid ki jaati hain.",
    "Rahu kaal me kalash ke saath pravesh nahi kiya jaata, aur kai parivaar Tuesday ko bhi taalte hain.",
    "Lagna: sthir lagna (Vrishabh, Simha, Vrishchik, Kumbh) ko pravesh ke liye achha maana jaata hai, jo sheher ke hisaab se alag samay par aata hai.",
  ],
};

const grihaFaqCommon = [
  {
    q: "Kya kiraye ke ghar ke liye bhi griha pravesh muhurat dekhna chahiye?",
    a: "Parampara me naye ghar, chahe apna ho ya kiraye ka, me pehli baar rehne jaane ko apurva pravesh hi maana jaata hai, isliye kai parivaar muhurat dekhte hain. Yeh vyakti aur parivaar ki aastha par hai; niyam kisi par zabardasti nahi.",
  },
  {
    q: "Agar muhurat wale din shift karna mumkin na ho to?",
    a: "Bahut se parivaar muhurat ke din kalash, deepak aur kuchh saamaan ke saath pravesh kar lete hain, aur baaki shifting baad me karte hain. Apne pandit ji se poochhiye ki aapki parampara me kya chalta hai.",
  },
];

export const grihaNov2026: LandingConfig = {
  url: "https://astroaura.ai/griha-pravesh-muhurat-november-2026",
  wide: true,
  title: "Griha Pravesh Muhurat November 2026: Dates | Aura AI",
  description:
    "November 2026 me griha pravesh ke shubh din: 11, 21, 25 aur 26 November, nakshatra aur tithi ke saath. Kahan panchang alag hain, woh bhi saaf likha hai yahan.",
  eyebrow: "Griha Pravesh Muhurat November 2026",
  h1Pre: "Griha Pravesh Muhurat November 2026:",
  h1Accent: "naye ghar ke shubh din",
  sub: "Chaar din jin par panchang sehmat hain, teen jin par nahi. Naye ghar me pehla kadam rakhne se pehle yeh table aur niyam dekh lijiye.",
  primaryCta: "Apni date kundli se check kijiye",
  secondaryCta: "Muhurat checker kholiye",
  lede: [
    "November 2026 me griha pravesh ke liye chaar din par panchang sehmat hain: Wednesday 11 November, Saturday 21 November, Wednesday 25 November aur Thursday 26 November. Inme se 11 November Chaturmas ke andar padta hai, jise kai parivaar griha pravesh ke liye taalte hain. Agar aap Chaturmas maante hain, to aapki asli shortlist 21, 25 aur 26 November hai.",
  ],
  heroFacts: ["4 sehmat din", "3 split din", "Chaturmas ka dhyan"],
  sections: [
    {
      h2: "November 2026: dates jin par panchang sehmat hain",
      table: {
        head: ["Date", "Nakshatra", "Tithi", "Dhyan dein"],
        rows: [
          ["11 Nov, Wed", "Anuradha", "Shukla Dwitiya", "Chaturmas ke andar"],
          ["21 Nov, Sat", "Revati, Ashwini", "Dwadashi, Trayodashi", "Devuthani ke baad; khidki chhoti"],
          ["25 Nov, Wed", "Rohini, Mrigashira", "Krishna Pratipada, Dwitiya", "Lagbhag pura din"],
          ["26 Nov, Thu", "Mrigashira", "Dwitiya, Tritiya", "Shaam se pehle tak"],
        ],
        caption:
          "Sources: DrikPanchang (VedicFeed ki list same hai) aur PanchangBodh 21, 25, 26 November par sehmat; 11 November DrikPanchang aur GaneshaSpeaks dono me hai.",
      },
      chips: [
        ...baseChips,
        { to: "/blog/diwali-2026-puja-muhurat", label: "Diwali 2026 puja muhurat" },
      ],
    },
    {
      h2: "Split dates: sirf ek publisher ki list me",
      body: [
        "Neeche ke din kisi ek list me hain, doosri me nahi. Inhe hum shubh ya ashubh nahi keh rahe; bas itna ki panchang sehmat nahi hain, to pandit ji se poochhe bina inhe final mat kijiye.",
      ],
      table: {
        head: ["Date", "Nakshatra", "Kis list me"],
        rows: [
          ["14 Nov, Sat", "Uttara Ashadha", "DrikPanchang, VedicFeed (Chaturmas ke andar)"],
          ["20 Nov, Fri", "Uttara Bhadrapada", "DrikPanchang, VedicFeed (Ekadashi ka din)"],
          ["28 Nov, Sat", "Pushya", "Sirf PanchangBodh"],
        ],
      },
    },
    grihaRules,
    {
      h2: "Pandit ji aur city panchang se confirm kijiye",
      body: [panditNote],
    },
    {
      h2: "Ghar ke saath apna chart",
      body: [
        "Kai parivaar griha pravesh ke liye ghar ke mukhiya ki kundli bhi dekhte hain: chal rahi dasha aur us din ka Chandra gochar. Free kundli banaiye aur AI jyotishi se poochhiye ki in dates me se aapke chart ke liye kaunsi behtar hai.",
      ],
      chips: [
        { to: "/mahadasha-calculator", label: "Mahadasha calculator" },
        { to: "/griha-pravesh-muhurat-december-2026", label: "Griha pravesh December 2026" },
      ],
    },
  ],
  faqs: [
    {
      q: "November 2026 me griha pravesh ke shubh din kaun se hain?",
      a: "11, 21, 25 aur 26 November 2026 par kam se kam do panchang sehmat hain. 11 November Chaturmas me hai, isliye jo parivaar Chaturmas maante hain unke liye 21, 25 aur 26 November bachte hain.",
    },
    {
      q: "Kya Chaturmas me griha pravesh kar sakte hain?",
      a: "Kai parampara me naye ghar ka pravesh Chaturmas me nahi kiya jaata, jabki kuchh panchang lists phir bhi dates deti hain. Yeh parivaar ki parampara ka sawaal hai; apne pandit ji se poochhiye.",
    },
    {
      q: "Griha pravesh ka samay kaise pata karein?",
      a: "Lagna aur Rahu kaal sheher ke sunrise par depend karte hain. Humare muhurat checker me apna sheher chuniye, ya us din ka panchang dekhiye.",
    },
    ...grihaFaqCommon,
  ],
  schemaName: "Griha Pravesh Muhurat November 2026",
  schemaDescription:
    "Griha pravesh (house warming) dates in November 2026 agreed by at least two panchang publishers: 11, 21, 25 and 26 November, plus the dates where publishers split.",
  finalHeading: "Naya ghar, sahi shuruaat",
  finalText:
    "Free kundli banaiye aur dekhiye ki aapki dasha ke hisaab se kaunsa din behtar baithta hai.",
  articleSection: "Muhurat",
  disclaimer: pmDisclaimer,
  relatedLinks: [
    { to: "/griha-pravesh-muhurat-december-2026", label: "griha pravesh muhurat December 2026" },
    { to: "/vivah-muhurat-november-2026", label: "vivah muhurat November 2026" },
  ],
};

export const grihaDec2026: LandingConfig = {
  url: "https://astroaura.ai/griha-pravesh-muhurat-december-2026",
  wide: true,
  title: "Griha Pravesh Muhurat December 2026: Dates | Aura AI",
  description:
    "December 2026 griha pravesh: 2, 3, 4, 11, 12 December Kharmas se pehle; 18, 19, 30 December Kharmas ke andar. Nakshatra, tithi aur panchang split yahan.",
  eyebrow: "Griha Pravesh Muhurat December 2026",
  h1Pre: "Griha Pravesh Muhurat December 2026:",
  h1Accent: "Kharmas se pehle aur baad",
  sub: "Paanch din Kharmas se pehle, teen Kharmas ke andar. Kharmas maanne wale parivaron ke liye pehli list hi asli list hai.",
  primaryCta: "Apni date kundli se check kijiye",
  secondaryCta: "Muhurat checker kholiye",
  lede: [
    "December 2026 me griha pravesh ke aath din par DrikPanchang aur PanchangBodh sehmat hain. Inme se paanch, yaani 2, 3, 4, 11 aur 12 December, 16 December ko Kharmas lagne se pehle hain. Baaki teen, 18, 19 aur 30 December, Kharmas ke andar padte hain. Dono lists inhe dikhati hain, lekin bahut se parivaar aur pandit Kharmas me naye ghar me pravesh nahi karte, isliye humne inhe alag mark kiya hai.",
  ],
  heroFacts: ["5 din Kharmas se pehle", "3 din Kharmas me", "Kharmas: 16 Dec se"],
  sections: [
    {
      h2: "December 2026: dates jin par panchang sehmat hain",
      table: {
        head: ["Date", "Nakshatra", "Tithi", "Dhyan dein"],
        rows: [
          ["2 Dec, Wed", "Uttara Phalguni", "Navami, Dashami", "Raat ka muhurat"],
          ["3 Dec, Thu", "Uttara Phalguni", "Dashami", "Subah ka muhurat"],
          ["4 Dec, Fri", "Chitra (PanchangBodh: Hasta bhi)", "Ekadashi", "Kharmas se pehle"],
          ["11 Dec, Fri", "Uttara Ashadha (PanchangBodh: Purva Ashadha)", "Tritiya", "Raat ka muhurat"],
          ["12 Dec, Sat", "Uttara Ashadha", "Tritiya", "Kharmas se pehle aakhri din"],
          ["18 Dec, Fri", "Revati", "Dashami", "Kharmas ke andar"],
          ["19 Dec, Sat", "Revati", "Dashami", "Kharmas ke andar"],
          ["30 Dec, Wed", "Uttara Phalguni", "Saptami", "Kharmas ke andar"],
        ],
        caption:
          "Sources: DrikPanchang (VedicFeed ki list same hai) aur PanchangBodh, dono me aathon dates. 11 December ka nakshatra dono me alag likha hai.",
      },
      chips: [
        ...baseChips,
        { to: "/blog/diwali-2026-puja-muhurat", label: "Diwali 2026 puja muhurat" },
      ],
    },
    {
      h2: "Split dates: sirf ek publisher ki list me",
      body: [
        "Yeh din kisi ek list me hain. Inhe pandit ji se confirm kiye bina final mat kijiye. Sab Kharmas ke andar bhi padte hain.",
      ],
      table: {
        head: ["Date", "Kis list me"],
        rows: [
          ["14 Dec, Mon", "Sirf PanchangBodh"],
          ["23 Dec, Wed", "Sirf GaneshaSpeaks"],
          ["26 Dec, Sat", "Sirf PanchangBodh"],
          ["31 Dec, Thu", "Sirf PanchangBodh"],
        ],
      },
    },
    {
      h2: "Kharmas aur griha pravesh",
      body: [
        "Wednesday, 16 December 2026 ko Surya Dhanu rashi me jaata hai. Dhanu Sankranti se Makar Sankranti tak ka yeh mahina Kharmas kehlata hai, aur parampara me ise naye shubh aarambh ke liye theek nahi maana jaata. Kuchh panchang griha pravesh ke liye sirf nakshatra aur tithi ki shuddhi dekhte hain aur Kharmas ki dates bhi de dete hain; kuchh parivaar inhe sirf dvandva pravesh (renovation ke baad) ke liye use karte hain.",
        "Seedhi salah: agar aapke parivaar me Kharmas maana jaata hai, to 12 December tak ki date chuniye, warna Makar Sankranti ke baad January 2027 ka intezaar kijiye.",
      ],
      chips: [{ to: "/makar-sankranti-2027", label: "Makar Sankranti 2027" }],
    },
    grihaRules,
    {
      h2: "Pandit ji aur city panchang se confirm kijiye",
      body: [panditNote],
      chips: [{ to: "/griha-pravesh-muhurat-november-2026", label: "Griha pravesh November 2026" }],
    },
  ],
  faqs: [
    {
      q: "December 2026 me griha pravesh ke shubh din kaun se hain?",
      a: "Kharmas se pehle 2, 3, 4, 11 aur 12 December. 18, 19 aur 30 December bhi panchang lists me hain, lekin woh Kharmas ke andar padte hain.",
    },
    {
      q: "Kya Kharmas me griha pravesh kar sakte hain?",
      a: "Kai parampara me nahi. Kuchh panchang phir bhi dates dete hain. Agar aapke parivaar me Kharmas maana jaata hai, to 12 December tak ya Makar Sankranti ke baad ki date chuniye.",
    },
    {
      q: "Kharmas 2026 kab se hai?",
      a: "Wednesday, 16 December 2026 ko Dhanu Sankranti se, aur Makar Sankranti tak.",
    },
    ...grihaFaqCommon,
  ],
  schemaName: "Griha Pravesh Muhurat December 2026",
  schemaDescription:
    "Griha pravesh dates in December 2026: 2, 3, 4, 11 and 12 December before Kharmas; 18, 19 and 30 December fall inside Kharmas, which begins 16 December.",
  finalHeading: "Naya ghar, sahi shuruaat",
  finalText:
    "Free kundli banaiye aur dekhiye ki aapki dasha ke hisaab se kaunsa din behtar baithta hai.",
  articleSection: "Muhurat",
  disclaimer: pmDisclaimer,
  relatedLinks: [
    { to: "/griha-pravesh-muhurat-november-2026", label: "griha pravesh muhurat November 2026" },
    { to: "/vivah-muhurat-december-2026", label: "vivah muhurat December 2026" },
  ],
};

/* ----------------------------------------------------------------- MUNDAN */

const mundanRules = {
  h2: "Mundan (chudakarana) muhurat classically kaise chuna jaata hai",
  body: [
    "Mundan solah sanskaron me se ek hai, jise shastron me chudakarana kehte hain. Grihya sutron aur muhurat granthon ke niyam yeh hain, lekin har parivaar ki apni kul-parampara bhi hoti hai, jo aksar inse upar maani jaati hai.",
  ],
  bullets: [
    "Umra: aam taur par pehle, teesre ya paanchve saal me, yaani odd varsh me. Kai parivaar ise kuldevta ya teerth par karte hain.",
    "Chaturmas aur Kharmas: zyaadatar parampara in dono me mundan nahi karti.",
    "Nakshatra: Ashwini, Mrigashira, Punarvasu, Pushya, Hasta, Chitra, Swati, Shravana, Dhanishtha aur Shatabhisha aksar chune jaate hain.",
    "Din: Monday, Wednesday, Thursday aur Friday ko achha maana jaata hai; Tuesday aur Saturday kai lists me avoid hote hain.",
    "Tithi: Amavasya, Purnima aur rikta tithiyan (Chaturthi, Navami, Chaturdashi) aam taur par avoid hoti hain.",
    "Rahu kaal aur Bhadra me baal nahi utaare jaate.",
  ],
};

const mundanSplitNote = {
  h2: "Ek zaroori baat: panchang yahan sehmat nahi hain",
  body: [
    "DrikPanchang mundan ki saal bhar ki list publish nahi karta. GaneshaSpeaks aur VedicFeed (Astrotalk ki list VedicFeed jaisi hi hai) is mahine dates dete hain, jo table me hain. Lekin PanchangBodh November aur December 2026 dono ko mundan ke liye 'nishiddha' mahina maanta hai aur ek bhi date nahi deta, Chaturmas, Kharmas aur grah-asta jaise kaaranon se. Astrosight ki November list bhi bilkul alag hai.",
    "Saaf shabdon me: is mahine mundan karna hai ya nahi, yeh aapke parivaar ki parampara aur pandit ji tay karenge. Hum koi ek list ko 'sahi' nahi keh rahe.",
  ],
};

const mundanFaqCommon = [
  {
    q: "Mundan kis umra me karna chahiye?",
    a: "Parampara me pehle, teesre ya paanchve saal me, yaani odd varsh me. Kai parivaar kuldevta ke sthan par ya kisi teerth par karte hain, aur wahan apni kul-parampara ka niyam chalta hai.",
  },
  {
    q: "Websites par mundan ki dates itni alag kyun hain?",
    a: "Kyunki kuch publishers Chaturmas aur Kharmas ko filter karte hain aur kuch nahi, aur nakshatra-tithi ke niyam bhi alag tolte hain. Isliye humne sirf do lists me sehmat dates rakhi hain, aur jahan koi panchang zero dates deta hai, woh bhi bataya hai.",
  },
];

export const mundanNov2026: LandingConfig = {
  url: "https://astroaura.ai/mundan-muhurat-november-2026",
  wide: true,
  title: "Mundan Muhurat November 2026: Dates Aur Niyam | Aura AI",
  description:
    "November 2026 mundan muhurat: do lists 1, 6, 11, 16, 26 November dete hain, lekin chaar Chaturmas me hain aur ek panchang zero deta hai. Sach yahan.",
  eyebrow: "Mundan Muhurat November 2026",
  h1Pre: "Mundan Muhurat November 2026:",
  h1Accent: "dates aur ek zaroori split",
  sub: "Do publishers paanch din dete hain, ek panchang ek bhi nahi. Chaturmas ke baad sirf ek date bachti hai. Yahan sab saaf likha hai.",
  primaryCta: "Bachche ki free kundli banaiye",
  secondaryCta: "Muhurat checker kholiye",
  lede: [
    "November 2026 me mundan ke liye GaneshaSpeaks aur VedicFeed dono 1, 6, 11, 16 aur 26 November dete hain. Lekin pehle chaar din Chaturmas ke andar hain, jab zyaadatar parampara mundan nahi karti, aur PanchangBodh poore November ko mundan ke liye nishiddha maanta hai. Agar aap Chaturmas maante hain, to is mahine ki ek hi date bachti hai: Thursday, 26 November.",
  ],
  heroFacts: ["5 dates, 2 lists me", "4 Chaturmas me", "1 panchang: zero"],
  sections: [
    {
      h2: "November 2026: dates jin par do lists sehmat hain",
      table: {
        head: ["Date", "Nakshatra", "Tithi", "Dhyan dein"],
        rows: [
          ["1 Nov, Sun", "Pushya", "Krishna Saptami", "Chaturmas ke andar"],
          ["6 Nov, Fri", "Hasta", "Krishna Dwadashi", "Chaturmas ke andar"],
          ["11 Nov, Wed", "Anuradha", "Shukla Dwitiya", "Chaturmas ke andar"],
          ["16 Nov, Mon", "Shravana", "Shukla Saptami", "Chaturmas ke andar"],
          ["26 Nov, Thu", "Mrigashira", "Krishna Dwitiya", "Devuthani ke baad"],
        ],
        caption:
          "Dates: GaneshaSpeaks aur VedicFeed dono ki lists. Din, tithi aur nakshatra DrikPanchang ke New Delhi month panchang se (sunrise ke samay), kyunki publishers ke apne nakshatra labels usse match nahi kar rahe the.",
      },
      chips: [
        ...baseChips,
        { to: "/naamkaran-muhurat-november-2026", label: "Naamkaran muhurat November 2026" },
      ],
    },
    mundanSplitNote,
    mundanRules,
    {
      h2: "Pandit ji aur city panchang se confirm kijiye",
      body: [panditNote],
    },
    {
      h2: "Bachche ki kundli se dekhiye",
      body: [
        "Kai pandit mundan ke din bachche ke janm nakshatra se us din ke Chandra ki sthiti (tara bal) bhi dekhte hain. Iske liye bachche ki kundli chahiye. Free kundli banaiye, aur janm nakshatra ek click me dekhiye.",
      ],
      chips: [
        { to: "/nakshatra-calculator", label: "Nakshatra calculator" },
        { to: "/mundan-muhurat-december-2026", label: "Mundan muhurat December 2026" },
      ],
    },
  ],
  faqs: [
    {
      q: "November 2026 me mundan ka shubh din kaunsa hai?",
      a: "GaneshaSpeaks aur VedicFeed dono 1, 6, 11, 16 aur 26 November dete hain. Inme se sirf 26 November Chaturmas ke baad hai. PanchangBodh November me koi date nahi deta. Final din apne pandit ji se tay kijiye.",
    },
    {
      q: "Kya Chaturmas me mundan kar sakte hain?",
      a: "Zyaadatar parampara me nahi. Kuchh lists phir bhi dates deti hain, isliye humne aise din table me alag mark kiye hain.",
    },
    ...mundanFaqCommon,
  ],
  schemaName: "Mundan Muhurat November 2026",
  schemaDescription:
    "Mundan (chudakarana) dates for November 2026 listed by two publishers, with tithi and nakshatra from DrikPanchang, Chaturmas flags, and an honest note where panchangs disagree.",
  finalHeading: "Bachche ka pehla sanskar, sahi din par",
  finalText: "Bachche ki free kundli banaiye aur janm nakshatra ke saath din chuniye.",
  articleSection: "Muhurat",
  disclaimer: pmDisclaimer,
  relatedLinks: [
    { to: "/mundan-muhurat-december-2026", label: "mundan muhurat December 2026" },
    { to: "/naamkaran-muhurat-november-2026", label: "naamkaran muhurat November 2026" },
  ],
};

export const mundanDec2026: LandingConfig = {
  url: "https://astroaura.ai/mundan-muhurat-december-2026",
  wide: true,
  title: "Mundan Muhurat December 2026: Dates Aur Niyam | Aura AI",
  description:
    "December 2026 mundan muhurat: 4 aur 14 December Kharmas se pehle; 19, 25, 31 December Kharmas me. Ek panchang zero dates deta hai. Puri jaankari yahan.",
  eyebrow: "Mundan Muhurat December 2026",
  h1Pre: "Mundan Muhurat December 2026:",
  h1Accent: "Kharmas se pehle do din",
  sub: "Do lists paanch din deti hain, lekin teen Kharmas me hain. Kharmas maanne wale parivaron ke liye 4 aur 14 December hi bachte hain.",
  primaryCta: "Bachche ki free kundli banaiye",
  secondaryCta: "Muhurat checker kholiye",
  lede: [
    "December 2026 me mundan ke liye GaneshaSpeaks aur VedicFeed dono 4, 14, 19, 25 aur 31 December dete hain. 16 December ko Kharmas shuru hota hai, isliye aakhri teen din uske andar hain, jab zyaadatar parampara mundan nahi karti. PanchangBodh poore December ko mundan ke liye nishiddha maanta hai. Kharmas maanne wale parivaron ke liye shortlist hai Friday 4 December aur Monday 14 December.",
  ],
  heroFacts: ["5 dates, 2 lists me", "3 Kharmas me", "Kharmas: 16 Dec se"],
  sections: [
    {
      h2: "December 2026: dates jin par do lists sehmat hain",
      table: {
        head: ["Date", "Nakshatra", "Tithi", "Dhyan dein"],
        rows: [
          ["4 Dec, Fri", "Hasta, phir Chitra", "Krishna Ekadashi", "Kharmas se pehle"],
          ["14 Dec, Mon", "Shravana, phir Dhanishtha", "Shukla Panchami", "Kharmas se pehle"],
          ["19 Dec, Sat", "Revati", "Shukla Dashami", "Kharmas ke andar; Saturday"],
          ["25 Dec, Fri", "Punarvasu", "Krishna Dwitiya", "Kharmas ke andar"],
          ["31 Dec, Thu", "Hasta", "Krishna Ashtami", "Kharmas ke andar"],
        ],
        caption:
          "Dates: GaneshaSpeaks aur VedicFeed dono ki lists. Din, tithi aur nakshatra DrikPanchang ke New Delhi month panchang se (sunrise ke samay).",
      },
      chips: [
        ...baseChips,
        { to: "/naamkaran-muhurat-december-2026", label: "Naamkaran muhurat December 2026" },
      ],
    },
    mundanSplitNote,
    {
      h2: "Kharmas aur mundan",
      body: [
        "Wednesday, 16 December 2026 ko Dhanu Sankranti hai, aur wahan se Makar Sankranti tak Kharmas maana jaata hai. Mundan jaise sanskar is dauraan aam taur par taale jaate hain. Agar 14 December tak sambhav nahi, to Makar Sankranti ke baad January 2027 ki dates ka intezaar kijiye.",
      ],
      chips: [{ to: "/makar-sankranti-2027", label: "Makar Sankranti 2027" }],
    },
    mundanRules,
    {
      h2: "Pandit ji aur city panchang se confirm kijiye",
      body: [panditNote],
      chips: [
        { to: "/nakshatra-calculator", label: "Nakshatra calculator" },
        { to: "/mundan-muhurat-november-2026", label: "Mundan muhurat November 2026" },
      ],
    },
  ],
  faqs: [
    {
      q: "December 2026 me mundan ka shubh din kaunsa hai?",
      a: "Do lists 4, 14, 19, 25 aur 31 December deti hain. Kharmas 16 December se hai, isliye Kharmas maanne wale parivaron ke liye 4 aur 14 December bachte hain. PanchangBodh December me koi date nahi deta.",
    },
    {
      q: "Kya Kharmas me mundan kar sakte hain?",
      a: "Zyaadatar parampara me nahi. Kuchh lists phir bhi dates deti hain; antim faisla parivaar ki parampara aur pandit ji ka hai.",
    },
    ...mundanFaqCommon,
  ],
  schemaName: "Mundan Muhurat December 2026",
  schemaDescription:
    "Mundan dates for December 2026 listed by two publishers: 4 and 14 December before Kharmas, 19, 25 and 31 December inside it, with an honest note where panchangs disagree.",
  finalHeading: "Bachche ka pehla sanskar, sahi din par",
  finalText: "Bachche ki free kundli banaiye aur janm nakshatra ke saath din chuniye.",
  articleSection: "Muhurat",
  disclaimer: pmDisclaimer,
  relatedLinks: [
    { to: "/mundan-muhurat-november-2026", label: "mundan muhurat November 2026" },
    { to: "/naamkaran-muhurat-december-2026", label: "naamkaran muhurat December 2026" },
  ],
};

/* -------------------------------------------------------------- NAAMKARAN */

const naamRules = {
  h2: "Naamkaran muhurat classically kaise chuna jaata hai",
  body: [
    "Naamkaran solah sanskaron me se ek hai, aur baaki sanskaron se alag ek baat me: yeh janm se juda hai. Grihya sutron me iska samay janm ke baad gyaarahve ya baarahve din ke aas paas bataya gaya hai, sutak samaapt hone ke baad. Is tarah ka monthly list tab kaam aata hai jab woh din nikal gaya ho, ya parivaar ise baad me ek samaroh ke roop me karna chahta ho.",
  ],
  bullets: [
    "Pehla akshar: naam ka pehla akshar bachche ke janm nakshatra ke charan (pada) se nikalta hai. Iske liye janm ka sahi samay chahiye.",
    "Nakshatra: Ashwini, Rohini, Mrigashira, Punarvasu, Pushya, Uttara Phalguni, Hasta, Chitra, Swati, Anuradha, Shravana, Dhanishtha, Uttara Ashadha, Uttara Bhadrapada aur Revati jaise chara, mridu aur kshipra nakshatra aksar chune jaate hain.",
    "Tithi: Amavasya aur rikta tithiyan aam taur par avoid hoti hain.",
    "Rahu kaal aur Bhadra me naam ki ghoshna nahi ki jaati.",
    "Chaturmas aur Kharmas: kyunki naamkaran janm se juda sanskar hai, kai parivaar ise in mahinon me bhi karte hain; kuchh taalte hain. Apni parampara ke hisaab se chuniye.",
  ],
};

const naamFaqCommon = [
  {
    q: "Naamkaran kab karna chahiye?",
    a: "Parampara me janm ke gyaarahve ya baarahve din ke aas paas, sutak ke baad. Agar woh din nikal gaya ho, to kisi shubh nakshatra aur tithi wale din kiya jaata hai, jaise is page ki list.",
  },
  {
    q: "Naam ka pehla akshar kaise nikalta hai?",
    a: "Bachche ke janm nakshatra ke charan se. Har nakshatra ke chaar charan hote hain aur har charan ka ek akshar. Janm ki date, samay aur jagah se nakshatra calculator turant bata deta hai.",
  },
];

export const naamNov2026: LandingConfig = {
  url: "https://astroaura.ai/naamkaran-muhurat-november-2026",
  wide: true,
  title: "Naamkaran Muhurat November 2026: Shubh Dates | Aura AI",
  description:
    "November 2026 naamkaran muhurat: 10 din par do panchang sehmat, nakshatra aur tithi ke saath. Naam ka pehla akshar janm nakshatra se kaise nikle, woh bhi.",
  eyebrow: "Naamkaran Muhurat November 2026",
  h1Pre: "Naamkaran Muhurat November 2026:",
  h1Accent: "bachche ke naam ke shubh din",
  sub: "Das din jin par do panchang lists sehmat hain, aur naam ka pehla akshar chunne ka classical tarika.",
  primaryCta: "Bachche ka nakshatra janiye",
  secondaryCta: "Muhurat checker kholiye",
  lede: [
    "November 2026 me naamkaran ke liye GaneshaSpeaks aur Astrotalk dono ki lists das dinon par sehmat hain: 1, 6, 11, 15, 16, 20, 22, 25, 26 aur 29 November. 25 aur 26 November teesri list, PanchangBodh, me bhi hain. Parampara me naamkaran janm ke gyaarahve ya baarahve din hota hai; yeh list tab kaam ki hai jab woh din nikal gaya ho ya samaroh baad me karna ho.",
  ],
  heroFacts: ["10 sehmat din", "Akshar: janm nakshatra se", "3 lists se cross-check"],
  sections: [
    {
      h2: "November 2026: dates jin par panchang sehmat hain",
      table: {
        head: ["Date", "Nakshatra", "Tithi"],
        rows: [
          ["1 Nov, Sun", "Pushya", "Krishna Saptami"],
          ["6 Nov, Fri", "Hasta", "Krishna Dwadashi"],
          ["11 Nov, Wed", "Anuradha", "Shukla Dwitiya"],
          ["15 Nov, Sun", "Uttara Ashadha", "Shukla Shashthi"],
          ["16 Nov, Mon", "Shravana", "Shukla Saptami"],
          ["20 Nov, Fri", "Uttara Bhadrapada", "Shukla Dashami, Ekadashi"],
          ["22 Nov, Sun", "Ashwini", "Shukla Trayodashi"],
          ["25 Nov, Wed", "Rohini", "Krishna Pratipada"],
          ["26 Nov, Thu", "Mrigashira", "Krishna Dwitiya"],
          ["29 Nov, Sun", "Pushya (subah tak)", "Krishna Shashthi"],
        ],
        caption:
          "Dates: GaneshaSpeaks aur Astrotalk dono ki lists; 25 aur 26 November PanchangBodh me bhi. Din, tithi aur nakshatra DrikPanchang ke New Delhi month panchang se (sunrise ke samay). DrikPanchang naamkaran ki saal bhar ki list publish nahi karta.",
      },
      chips: [
        ...baseChips,
        { to: "/nakshatra-calculator", label: "Nakshatra calculator" },
      ],
    },
    {
      h2: "Split dates",
      body: [
        "5 November (Thursday, Uttara Phalguni) Astrotalk aur PanchangBodh me hai, GaneshaSpeaks me nahi. 18 November sirf PanchangBodh me hai. Inhe pandit ji se poochhe bina final mat kijiye.",
      ],
    },
    naamRules,
    {
      h2: "Pandit ji aur city panchang se confirm kijiye",
      body: [panditNote],
    },
    {
      h2: "Pehle bachche ka janm nakshatra",
      body: [
        "Muhurat se pehle ka kadam hai bachche ka janm nakshatra aur charan janna, kyunki naam ka akshar wahin se aata hai. Janm ki date, samay aur jagah daaliye, nakshatra aur charan turant mil jaayenge, aur free kundli bhi.",
      ],
      chips: [
        { to: "/moon-sign-calculator", label: "Moon sign calculator" },
        { to: "/naamkaran-muhurat-december-2026", label: "Naamkaran muhurat December 2026" },
      ],
    },
  ],
  faqs: [
    {
      q: "November 2026 me naamkaran ke shubh din kaun se hain?",
      a: "1, 6, 11, 15, 16, 20, 22, 25, 26 aur 29 November 2026 par do panchang lists sehmat hain. Exact samay sheher ke hisaab se apne panchang ya pandit ji se lijiye.",
    },
    {
      q: "Kya Chaturmas me naamkaran ho sakta hai?",
      a: "Naamkaran janm se juda sanskar hai, isliye kai parivaar ise Chaturmas me bhi karte hain. Kuchh parampara taalti hain. Yeh parivaar ki parampara par hai.",
    },
    ...naamFaqCommon,
  ],
  schemaName: "Naamkaran Muhurat November 2026",
  schemaDescription:
    "Naming ceremony (naamkaran) dates for November 2026 agreed by two panchang publishers, with tithi and nakshatra from DrikPanchang and how the first letter of the name is chosen.",
  finalHeading: "Naam jo nakshatra se mel khaaye",
  finalText: "Bachche ki free kundli banaiye aur janm nakshatra ka akshar dekhiye.",
  articleSection: "Muhurat",
  disclaimer: pmDisclaimer,
  relatedLinks: [
    { to: "/naamkaran-muhurat-december-2026", label: "naamkaran muhurat December 2026" },
    { to: "/mundan-muhurat-november-2026", label: "mundan muhurat November 2026" },
  ],
};

export const naamDec2026: LandingConfig = {
  url: "https://astroaura.ai/naamkaran-muhurat-december-2026",
  wide: true,
  title: "Naamkaran Muhurat December 2026: Shubh Dates | Aura AI",
  description:
    "December 2026 naamkaran muhurat: 10 din par do panchang sehmat, nakshatra aur tithi ke saath. Kharmas me naamkaran ho sakta hai ya nahi, jawaab bhi yahan.",
  eyebrow: "Naamkaran Muhurat December 2026",
  h1Pre: "Naamkaran Muhurat December 2026:",
  h1Accent: "bachche ke naam ke shubh din",
  sub: "Das din jin par do panchang lists sehmat hain, aur Kharmas ka sawaal saaf shabdon me.",
  primaryCta: "Bachche ka nakshatra janiye",
  secondaryCta: "Muhurat checker kholiye",
  lede: [
    "December 2026 me naamkaran ke liye kam se kam do panchang lists das dinon par sehmat hain: 3, 4, 13, 14, 17, 20, 23, 25, 30 aur 31 December, aur inme 3, 4, 25, 30, 31 December teenon lists (GaneshaSpeaks, Astrotalk, PanchangBodh) me hain. 16 December se Kharmas hai; kyunki naamkaran janm se juda sanskar hai, kai parivaar ise Kharmas me bhi karte hain, jabki kuchh taalte hain.",
  ],
  heroFacts: ["10 sehmat din", "5 din teenon lists me", "Kharmas: 16 Dec se"],
  sections: [
    {
      h2: "December 2026: dates jin par panchang sehmat hain",
      table: {
        head: ["Date", "Nakshatra", "Tithi", "Lists"],
        rows: [
          ["3 Dec, Thu", "Uttara Phalguni, phir Hasta", "Krishna Dashami", "3"],
          ["4 Dec, Fri", "Hasta, phir Chitra", "Krishna Ekadashi", "3"],
          ["13 Dec, Sun", "Shravana", "Shukla Chaturthi", "2 (GS, AT)"],
          ["14 Dec, Mon", "Shravana, phir Dhanishtha", "Shukla Panchami", "2 (GS, PB)"],
          ["17 Dec, Thu", "Uttara Bhadrapada (dopahar se)", "Shukla Ashtami", "2 (GS, AT); Kharmas"],
          ["20 Dec, Sun", "Ashwini", "Shukla Ekadashi", "2 (GS, AT); Kharmas"],
          ["23 Dec, Wed", "Rohini, phir Mrigashira", "Chaturdashi, Purnima", "2 (GS, AT); Kharmas"],
          ["25 Dec, Fri", "Pushya (raat se)", "Krishna Dwitiya", "3; Kharmas"],
          ["30 Dec, Wed", "Uttara Phalguni, phir Hasta", "Krishna Saptami", "3; Kharmas"],
          ["31 Dec, Thu", "Hasta", "Krishna Ashtami", "3; Kharmas"],
        ],
        caption:
          "Din, tithi aur nakshatra DrikPanchang ke New Delhi month panchang se. 13 aur 14 December ki khidki ek hi lagataar muhurat hai jo 13 ki shaam se 14 ki subah tak chalti hai, isliye lists ise alag alag din par likhti hain. Lists: 3 = teenon (GaneshaSpeaks, Astrotalk, PanchangBodh); GS = GaneshaSpeaks, AT = Astrotalk, PB = PanchangBodh.",
      },
      chips: [
        ...baseChips,
        { to: "/nakshatra-calculator", label: "Nakshatra calculator" },
      ],
    },
    {
      h2: "Split dates",
      body: [
        "6 aur 16 December sirf Astrotalk me hain; 7, 12 aur 18 December sirf GaneshaSpeaks me. Inhe pandit ji se poochhe bina final mat kijiye.",
      ],
    },
    {
      h2: "Kharmas me naamkaran: ho sakta hai ya nahi",
      body: [
        "Wednesday, 16 December 2026 ko Dhanu Sankranti hai aur wahan se Makar Sankranti tak Kharmas. Vivah aur griha pravesh jaise naye aarambh Kharmas me roke jaate hain. Naamkaran ka mamla alag hai: yeh janm se juda sanskar hai, aur janm ke gyaarahve-baarahve din wala niyam Kharmas dekhkar nahi badalta. Isliye kai parivaar Kharmas me bhi naamkaran karte hain. Agar aapki parampara Kharmas me ise bhi taalti hai, to 3, 4, 13 aur 14 December aapki shortlist hai.",
      ],
      chips: [{ to: "/makar-sankranti-2027", label: "Makar Sankranti 2027" }],
    },
    naamRules,
    {
      h2: "Pandit ji aur city panchang se confirm kijiye",
      body: [panditNote],
      chips: [
        { to: "/moon-sign-calculator", label: "Moon sign calculator" },
        { to: "/naamkaran-muhurat-november-2026", label: "Naamkaran muhurat November 2026" },
      ],
    },
  ],
  faqs: [
    {
      q: "December 2026 me naamkaran ke shubh din kaun se hain?",
      a: "3, 4, 13, 14, 17, 20, 23, 25, 30 aur 31 December par kam se kam do panchang lists sehmat hain. 3, 4, 25, 30 aur 31 December teenon lists me hain.",
    },
    {
      q: "Kya Kharmas me naamkaran kar sakte hain?",
      a: "Kai parivaar karte hain, kyunki naamkaran janm se juda sanskar hai. Kuchh parampara ise bhi Kharmas ke baad karti hain. Apne pandit ji se poochhiye.",
    },
    ...naamFaqCommon,
  ],
  schemaName: "Naamkaran Muhurat December 2026",
  schemaDescription:
    "Naming ceremony dates for December 2026 agreed by at least two panchang publishers, with tithi and nakshatra from DrikPanchang and a plain answer on naamkaran during Kharmas.",
  finalHeading: "Naam jo nakshatra se mel khaaye",
  finalText: "Bachche ki free kundli banaiye aur janm nakshatra ka akshar dekhiye.",
  articleSection: "Muhurat",
  disclaimer: pmDisclaimer,
  relatedLinks: [
    { to: "/naamkaran-muhurat-november-2026", label: "naamkaran muhurat November 2026" },
    { to: "/mundan-muhurat-december-2026", label: "mundan muhurat December 2026" },
  ],
};

export const muhuratPages: { path: string; config: LandingConfig }[] = [
  { path: "/vivah-muhurat-november-2026", config: vivahNov2026 },
  { path: "/vivah-muhurat-december-2026", config: vivahDec2026 },
  { path: "/griha-pravesh-muhurat-november-2026", config: grihaNov2026 },
  { path: "/griha-pravesh-muhurat-december-2026", config: grihaDec2026 },
  { path: "/mundan-muhurat-november-2026", config: mundanNov2026 },
  { path: "/mundan-muhurat-december-2026", config: mundanDec2026 },
  { path: "/naamkaran-muhurat-november-2026", config: naamNov2026 },
  { path: "/naamkaran-muhurat-december-2026", config: naamDec2026 },
];
